import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button, Result, Typography } from 'antd';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * 页面级错误边界。
 *
 * 主要挡两类：
 *  1. chunk 加载失败——发版后旧 chunk 被替换，已开着的标签页点进没加载过的页面会 404。
 *     {@link lazyPage} 会先自动重载一次；如果重载后还失败（说明不是版本不一致），
 *     错误会冒到这里，给一个「重新加载」而不是白屏。
 *  2. 页面渲染时抛出的其它异常——同样别让整个后台白掉，侧边栏保留可用。
 */
export default class PageErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[PageErrorBoundary]', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isChunkError = /dynamically imported module|module script failed|Failed to fetch/i.test(
      error.message,
    );

    return (
      <Result
        status="warning"
        title={isChunkError ? '页面资源加载失败' : '页面出错了'}
        subTitle={
          isChunkError
            ? '通常是后台刚发布了新版本，当前页面用的还是旧资源。重新加载即可。'
            : '这一页渲染时抛了异常，重新加载或换个页面试试；持续出现请把下面的信息发给开发。'
        }
        extra={[
          <Button key="reload" type="primary" onClick={() => window.location.reload()}>
            重新加载
          </Button>,
          <Button key="reset" onClick={() => this.setState({ error: null })}>
            重试当前页
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          {error.message}
        </Typography.Paragraph>
      </Result>
    );
  }
}
